import { createSlice, PayloadAction } from '@reduxjs/toolkit';

/**
 * Mampara de privacidad del dashboard.
 *
 * El caso real: el dueño consulta sus números en el piso y pasa un empleado por
 * detrás. Esto NO es un permiso — quien puede abrir el dashboard puede picarle
 * al ojito y ver los montos. Es solo para que no estén a la vista de quien pasa.
 * Si algún día se quiere que un rol NO pueda verlos, eso es otra función.
 *
 * `otros` agrupa el resto de montos de la página (gráfica por hora, ingresos por
 * tipo de orden, top de productos, la tira de semana/mes/ticket promedio, el
 * resumen del asistente). No llevan ojito propio a propósito: un ojito por cada
 * etiqueta de una gráfica sería absurdo, y tapar tres cifras dejando otras siete
 * a la vista no serviría de nada.
 */
export type MoneyKey = 'today' | 'payments' | 'cash' | 'otros';

export const MONEY_KEYS: MoneyKey[] = ['today', 'payments', 'cash', 'otros'];

interface DashboardPrivacyState {
  hidden: MoneyKey[];
}

const STORAGE_KEY = 'dashboardHiddenMoney';

// Se lee al cargar el módulo, como themeSlice. Va en try/catch porque en modo
// privado o con el almacenamiento bloqueado el acceso puede lanzar.
const getInitialState = (): DashboardPrivacyState => {
  let hidden: MoneyKey[] = [];

  try {
    const saved = localStorage.getItem(STORAGE_KEY);
    if (saved) {
      const parsed = JSON.parse(saved);
      if (Array.isArray(parsed)) {
        hidden = parsed.filter((k): k is MoneyKey => MONEY_KEYS.includes(k));
      }
    }
  } catch {
    // Preferencia cosmética: si no se puede leer, se arranca mostrando todo.
  }

  return { hidden };
};

const persist = (hidden: MoneyKey[]) => {
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(hidden));
  } catch {
    // Sin persistencia la mampara sigue funcionando en esta sesión.
  }
};

const dashboardPrivacySlice = createSlice({
  name: 'dashboardPrivacy',
  initialState: getInitialState(),
  reducers: {
    toggleMoneyKey: (state, action: PayloadAction<MoneyKey>) => {
      const key = action.payload;
      state.hidden = state.hidden.includes(key)
        ? state.hidden.filter(k => k !== key)
        : [...state.hidden, key];
      persist(state.hidden);
    },
    // Semántica de "ocultar/mostrar todo": si queda algo visible, oculta todo;
    // solo cuando ya está todo oculto vuelve a mostrar. Es el gesto que importa
    // —un toque para tapar cuando se acerca alguien— y no deja estados raros.
    toggleAllMoney: (state) => {
      const allHidden = MONEY_KEYS.every(k => state.hidden.includes(k));
      state.hidden = allHidden ? [] : [...MONEY_KEYS];
      persist(state.hidden);
    },
  },
});

export const { toggleMoneyKey, toggleAllMoney } = dashboardPrivacySlice.actions;
export default dashboardPrivacySlice.reducer;
